import type { MouseEvent, ReactElement, ReactNode } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Tooltip } from "@similarweb/ui-components";
import { Flex, Stack } from "@similarweb/ui-components/foundations";

import { useScrollbarType } from "app/pages/sales-intelligence/hooks/useScrollbarType";

import type { FiltersEnrichedCategorizedGroup } from "../../types/category";
import { FilterCategorySectionTitle } from "../FilterCategorySectionTitle/FilterCategorySectionTitle";
import FiltersListLoader from "../FiltersListLoader/FiltersListLoader";

import { StyledFiltersList, StyledFiltersListWrapper, StyledViewMoreButton } from "./styles";

type CategorizedFiltersListProps<TItem> = {
    permanentFilters: readonly TItem[];
    groups: readonly FiltersEnrichedCategorizedGroup<TItem>[];
    renderFilterCard: (item: TItem) => ReactElement;
    isLoading?: boolean;
    footerSlot?: ReactNode;
    onOpenCategoryPanel?: (category: string, event?: MouseEvent<HTMLElement>) => void;
};

export const CategorizedFiltersList = <TItem,>({
    permanentFilters,
    groups,
    renderFilterCard,
    isLoading = false,
    footerSlot,
    onOpenCategoryPanel,
}: CategorizedFiltersListProps<TItem>): ReactElement => {
    const { translate } = useTranslate();

    const scrollbarType = useScrollbarType();

    return (
        <StyledFiltersListWrapper>
            <StyledFiltersList $hasOverlayScrollbar={scrollbarType === "overlay"}>
                {isLoading ? (
                    <FiltersListLoader rowNumber={6} />
                ) : (
                    <Stack
                        data-automation="filter-panel.categorized-items-wrapper"
                        gap="16px"
                    >
                        {permanentFilters.length > 0 ? (
                            <Stack gap="8px">{permanentFilters.map(renderFilterCard)}</Stack>
                        ) : null}

                        {groups.map(({ category, filters, hasHiddenFilters }) => {
                            const EmptyContent = category.emptyContent;

                            return (
                                <Stack
                                    key={category.id}
                                    gap="8px"
                                >
                                    <Flex
                                        alignItems="center"
                                        justifyContent="space-between"
                                    >
                                        <FilterCategorySectionTitle category={category} />

                                        {hasHiddenFilters ? (
                                            <Tooltip
                                                content={translate(
                                                    category.viewMoreTooltipKey ??
                                                        "si.filter-panel.category-view-more.tooltip.text",
                                                )}
                                            >
                                                <StyledViewMoreButton
                                                    dataAutomation={`filter-panel.category-view-more.${category.id}`}
                                                    iconName="filters-icon"
                                                    type="flat"
                                                    iconSize="xs"
                                                    onClick={(event?: MouseEvent<HTMLElement>) =>
                                                        onOpenCategoryPanel?.(category.id, event)
                                                    }
                                                />
                                            </Tooltip>
                                        ) : null}
                                    </Flex>

                                    {filters.length > 0 ? (
                                        filters.map(renderFilterCard)
                                    ) : EmptyContent ? (
                                        <EmptyContent />
                                    ) : null}
                                </Stack>
                            );
                        })}
                    </Stack>
                )}
            </StyledFiltersList>

            {footerSlot}
        </StyledFiltersListWrapper>
    );
};
